import { useState, useMemo, useCallback, useEffect } from 'react';
import { Settings2, RefreshCw, Activity, CheckCircle2, XCircle, Clock, AlertTriangle, ChevronDown, ChevronRight, Coins, FileText, Trash2, Plus, Save, X, Users, Table2 } from 'lucide-react';
import UserManagementTab from './UserManagementTab';
import { FieldsTab } from './FieldsTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { cn } from '@/lib/utils';

/* --- Types --- */
interface EtlRun {
  id: string; job_name: string; source_system: string; entity_code: string;
  status: string; started_at: string | null; completed_at: string | null;
  rows_extracted: number; rows_loaded: number; duration_ms: number;
  error_message: string | null;
}

interface EtlStatusResponse {
  runs: EtlRun[];
  summary: { total: number; completed: number; failed: number; running: number };
  latestByJob: EtlRun[];
  failures: EtlRun[];
}

interface Entity { id: string; code: string; name: string; }

interface FxRatesData {
  rate_date: string; base_currency: string; rates: Record<string, number>;
  extracted_at: string; history: { rate_date: string; rates: Record<string, number> }[];
}

/* --- Helpers --- */
function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  completed: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  failed: { bg: 'bg-red-50', text: 'text-red-700' },
  running: { bg: 'bg-amber-50', text: 'text-amber-700' },
};

export default function Settings() {
  // Read the signed-in user from localStorage so the Users tab can hide
  // itself / disable self-destructive actions. Same pattern as
  // src/Dashboard.tsx — the JWT lives in fin_auth_token, the decoded
  // user profile in fin_auth_user.
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('fin_auth_user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return { id: String(u.id || ''), role: String(u.role || '') };
    } catch {
      return null;
    }
  }, []);
  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'admin';

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
          <Settings2 size={24} /> FIN Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">ETL pipeline status, monthly plans, FX rates, client mapping{isAdmin ? ', and users' : ''}</p>
      </div>

      <Tabs defaultValue="etl">
        <TabsList className="flex-wrap">
          <TabsTrigger value="etl" className="gap-2"><Activity size={14} /> ETL Status</TabsTrigger>
          <TabsTrigger value="monthly-plan" className="gap-2"><FileText size={14} /> Monthly Plans</TabsTrigger>
          <TabsTrigger value="fx" className="gap-2"><Coins size={14} /> FX Rates</TabsTrigger>
          <TabsTrigger value="fields" className="gap-2"><Table2 size={14} /> Fields</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="users" className="gap-2"><Users size={14} /> Users</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="etl" className="mt-4">
          <EtlStatusTab />
        </TabsContent>
        <TabsContent value="monthly-plan" className="mt-4">
          <MonthlyPlanTab />
        </TabsContent>
        <TabsContent value="fx" className="mt-4">
          <FxRatesTab />
        </TabsContent>
        <TabsContent value="fields" className="mt-4">
          <FieldsTab isAdmin={isAdmin} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="users" className="mt-4">
            <UserManagementTab
              currentUserId={currentUser?.id || null}
              currentUserRole={currentUser?.role || null}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* --- ETL Status Tab --- */
function EtlStatusTab() {
  const { data, loading, refresh } = useFetch<EtlStatusResponse>('/api/fin/etl-status');

  if (loading && !data) return <div className="py-8 text-center text-muted-foreground">Loading ETL status...</div>;
  if (!data) return <div className="py-8 text-center text-muted-foreground">Failed to load ETL data</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Badge variant="secondary">{data.summary.total} runs (7d)</Badge>
          <Badge className="bg-emerald-100 text-emerald-800">{data.summary.completed} OK</Badge>
          {data.summary.failed > 0 && <Badge variant="destructive">{data.summary.failed} failed</Badge>}
          {data.summary.running > 0 && <Badge className="bg-amber-100 text-amber-800">{data.summary.running} running</Badge>}
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => refresh()}>
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Latest by job */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Job Status Board</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.latestByJob.map(run => {
              const style = STATUS_STYLES[run.status] || { bg: 'bg-muted', text: 'text-muted-foreground' };
              return (
                <div key={run.id} className={cn('rounded-lg p-3 border', style.bg)}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">{run.job_name}</span>
                    <Badge className={cn('text-[10px]', style.text)}>{run.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div>{run.source_system} / {run.entity_code}</div>
                    <div>Last run: {formatRelativeTime(run.started_at)} ({formatDuration(run.duration_ms)})</div>
                    <div>Rows: {run.rows_extracted} extracted, {run.rows_loaded} loaded</div>
                  </div>
                  {run.error_message && (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2 truncate">{run.error_message}</div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent failures */}
      {data.failures.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-red-600">Recent Failures</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Job</TableHead>
                  <TableHead className="text-xs">Source</TableHead>
                  <TableHead className="text-xs">Started</TableHead>
                  <TableHead className="text-xs">Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.failures.map(f => (
                  <TableRow key={f.id}>
                    <TableCell className="text-sm font-medium">{f.job_name}</TableCell>
                    <TableCell className="text-sm">{f.source_system} / {f.entity_code}</TableCell>
                    <TableCell className="text-sm">{formatDateTime(f.started_at)}</TableCell>
                    <TableCell className="text-sm text-red-600 max-w-xs truncate">{f.error_message || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* --- FX Rates Tab --- */
const BASE_CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CHF', 'AED'] as const;

function formatRateDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function FxRatesTab() {
  const { data, loading, refresh } = useFetch<FxRatesData>('/api/fin/fx-rates');
  const [refreshing, setRefreshing] = useState(false);
  const { baseCurrency, setBaseCurrency } = useCurrency();

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const res = await fetch('/api/fin/fx-rates', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      await refresh();
    } catch (err) { console.error(err); alert(err instanceof Error ? err.message : 'Failed to fetch rates'); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {data?.rate_date && (
            <Badge variant="secondary" className="text-xs">
              Rates as of {formatRateDate(data.rate_date)} (source: {data.base_currency})
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Fetching...' : 'Fetch Latest'}
        </Button>
      </div>

      {/* App base currency setting */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Application Base Currency</div>
              <div className="text-xs text-muted-foreground mt-0.5">Used as default display currency across all dashboards</div>
            </div>
            <Select value={baseCurrency} onValueChange={setBaseCurrency}>
              <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BASE_CURRENCY_OPTIONS.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {data?.rates ? (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(data.rates)
                .filter(([ccy]) => ccy !== baseCurrency)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([ccy, rawRate]) => {
                  // Raw rates are per 1 USD. Recalculate relative to the selected base currency.
                  const baseRate = baseCurrency === 'USD' ? 1 : Number(data.rates[baseCurrency]);
                  const numRate = Number(rawRate);
                  if (!numRate || isNaN(numRate)) return null;
                  const displayRate = baseRate ? numRate / baseRate : numRate;
                  return (
                    <div key={ccy} className="p-3 rounded-lg bg-muted/30 border">
                      <div className="text-sm font-medium text-foreground">{ccy}</div>
                      <div className="text-lg font-mono font-bold text-foreground">
                        {ccy === 'BTC' || ccy === 'ETH' ? displayRate.toFixed(8) : displayRate.toFixed(4)}
                      </div>
                      <div className="text-xs text-muted-foreground">per 1 {baseCurrency}</div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Coins size={24} className="text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No FX rates loaded yet. Click "Fetch Latest" to load.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* --- Monthly Plan Tab --- */
interface MonthlyPlanEntry {
  id: string;
  entity_id: string;
  entity_name: string;
  month: string;
  type: string;
  category: string | null;
  description: string | null;
  amount: number;
  currency: string;
  entered_by: string | null;
  entered_at: string;
}

interface PlanCategory { id: string; name: string; type: string; is_active: boolean; }

function MonthlyPlanTab() {
  const currentMonth = new Date().toISOString().substring(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth + '-01');
  const { data, loading, refresh } = useFetch<{ entries: MonthlyPlanEntry[] }>(`/api/fin/monthly-plan?month=${selectedMonth}`);
  const { data: entities } = useFetch<Entity[]>('/api/fin/entities');
  const { data: txCategories } = useFetch<PlanCategory[]>('/api/fin/categories');

  const [entityId, setEntityId] = useState('');
  const [type, setType] = useState<string>('revenue');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Filter categories by type
  const filteredCategories = (txCategories || []).filter(c => {
    if (type === 'revenue') return c.type === 'income' || c.type === 'transfer';
    return c.type === 'expense' || c.type === 'transfer';
  });

  const handleSubmit = async () => {
    if (!entityId || !amount || !category) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/fin/monthly-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_id: entityId,
          month: selectedMonth,
          type,
          category: category || null,
          description: description || null,
          amount: parseFloat(amount),
          currency: currency.toUpperCase(),
          entered_by: 'dashboard',
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setAmount('');
      setCategory('');
      setDescription('');
      await refresh();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await fetch('/api/fin/monthly-plan', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      if (!res.ok) throw new Error('Failed');
      await refresh();
    } catch (err) { console.error(err); }
    finally { setDeletingId(null); }
  };

  // Generate month options (current year)
  const monthOptions = [];
  const year = new Date().getFullYear();
  for (let m = 0; m < 12; m++) {
    const d = `${year}-${String(m + 1).padStart(2, '0')}-01`;
    const label = new Date(year, m).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: d, label });
  }

  const entries = data?.entries || [];
  const totalRevenue = entries.filter(e => e.type === 'revenue').reduce((s, e) => s + Number(e.amount), 0);
  const totalExpenses = entries.filter(e => e.type === 'expense').reduce((s, e) => s + Number(e.amount), 0);

  // Group entries by entity
  const grouped = entries.reduce<Record<string, MonthlyPlanEntry[]>>((acc, e) => {
    const key = e.entity_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  const fmt = (n: number) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="shadow-xs">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase mb-1">Planned Revenue</div>
            <div className="text-xl font-bold text-emerald-600">{fmt(totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card className="shadow-xs">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase mb-1">Planned Expenses</div>
            <div className="text-xl font-bold text-red-500">{fmt(Math.abs(totalExpenses))}</div>
          </CardContent>
        </Card>
        <Card className="shadow-xs">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase mb-1">Net</div>
            <div className={`text-xl font-bold ${totalRevenue + totalExpenses >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {fmt(totalRevenue + totalExpenses)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Entry form */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Add Monthly Plan Entry</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label>Month</Label>
              <Select value={selectedMonth} onValueChange={v => setSelectedMonth(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Entity</Label>
              <Select value={entityId || '__none__'} onValueChange={v => setEntityId(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select...</SelectItem>
                  {(entities || []).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={v => { setType(v); setCategory(''); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Revenue</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category || '__none__'} onValueChange={v => setCategory(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select...</SelectItem>
                  {filteredCategories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <div className="flex gap-2">
                <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="flex-1" />
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['EUR', 'USD', 'GBP', 'CHF', 'AED', 'USDT', 'USDC'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
            </div>
            <div className="flex items-end">
              <Button onClick={handleSubmit} disabled={submitting || !entityId || !amount || !category}>
                <Plus size={14} className="mr-1" /> {submitting ? 'Saving...' : 'Add Entry'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Month selector & plan label */}
      <div className="flex items-center gap-4">
        <h3 className="text-sm font-medium">
          Plan for {new Date(selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
      </div>

      {/* Grouped entries table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Entity / Category</TableHead>
              <TableHead className="text-xs">Type</TableHead>
              <TableHead className="text-xs">Description</TableHead>
              <TableHead className="text-xs text-right">Amount</TableHead>
              <TableHead className="text-xs">CCY</TableHead>
              <TableHead className="text-xs w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([entityName, items]) => {
              const entRev = items.filter(e => e.type === 'revenue').reduce((s, e) => s + Number(e.amount), 0);
              const entExp = items.filter(e => e.type === 'expense').reduce((s, e) => s + Number(e.amount), 0);
              return (
                <>{/* Fragment for entity group */}
                  <TableRow className="bg-muted/20 border-t">
                    <TableCell colSpan={3} className="text-sm font-semibold">{entityName}</TableCell>
                    <TableCell className="text-sm text-right font-mono font-medium">
                      <span className="text-emerald-600">{fmt(entRev)}</span>
                      {' / '}
                      <span className="text-red-500">{fmt(Math.abs(entExp))}</span>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {items.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm pl-6">{e.category || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={e.type === 'revenue' ? 'default' : 'destructive'} className="text-[10px]">{e.type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{e.description || '-'}</TableCell>
                      <TableCell className="text-sm text-right font-mono">
                        {fmt(Number(e.amount))}
                      </TableCell>
                      <TableCell className="text-sm">{e.currency}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500"
                          onClick={() => handleDelete(e.id)} disabled={deletingId === e.id}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              );
            })}
            {entries.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No plan entries for this month</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
